import { useState, useMemo, useEffect } from "react";
// === ADDED BY SAMPATH – 2026-01-29 === (useEffect added)
// === END SAMPATH ===

import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { useAppSelector, useAppDispatch } from "hooks/store";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import { setdeliveryType } from "store/slices/main.slice";

// === ADDED BY SAMPATH – 2026-01-29 ===
// Icons   style
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useCachedImage } from "hooks/useCachedImage";
import { resolveUploadAssetUrl } from "utils/media";
// === END SAMPATH ===

interface OrderTypePopupProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (orderType: "pickup" | "delivery") => void;
}

const OrderTypePopup: React.FC<OrderTypePopupProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const dispatch = useAppDispatch();
  const { settings, companyData, deliveryType } = useAppSelector(({ main }) => main);
  const resolvedLogoUrl = useMemo(
    () => resolveUploadAssetUrl(companyData?.logoUrl),
    [companyData?.logoUrl]
  );
  const logoUrl = useCachedImage(resolvedLogoUrl, { cacheKey: "company-logo" });

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Use Redux deliveryType as the source of truth (same as Cart.tsx)
  const initialType = (deliveryType === "pickup" ? "pickup" : "delivery") as
    | "pickup"
    | "delivery";
  // === END SAMPATH ===

  const [orderType, setOrderType] = useState<"pickup" | "delivery">(initialType);

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Respect store closed flags (same logic style as Cart.tsx)
  const isPickupDisabled = !!settings?.ClosePickupOrder;
  const isDeliveryDisabled = !!settings?.CloseDeliveryOrder;

  // When BOTH are disabled, show one clear red message too
  const isAllDisabled = isPickupDisabled && isDeliveryDisabled;
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-05 ===
  // Prevent closing the modal when clicking the backdrop (background area)
  const handleModalClose = (_event: any, reason?: string) => {
    if (reason === "backdropClick") return; // disable background click close
    onClose(); // keep other close behaviors (e.g., ESC) working
  };
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Build a clean, centered location line (same fields style as ToolBar.tsx)
  const locationText = useMemo(() => {
    const a1 = (companyData?.address1 ?? "").trim();
    const a2 = (companyData?.address2 ?? "").trim();
    const full = `${a1}${a1 && a2 ? " " : ""}${a2}`.trim();
    return full;
  }, [companyData?.address1, companyData?.address2]);

  const showLocationUnderName =
    !!settings?.DisplayLocationOnCheckout && !!locationText;

  // Better red messages per order type
  const pickupUnavailableLabel = "Pickup will be available soon";
  const deliveryUnavailableLabel = "Delivery will be available soon";
  const allUnavailableLabel = "We’re not accepting orders today";
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Click-to-start flow (no Continue button)
  const handleStartOrder = (type: "pickup" | "delivery") => {
    if (type === "pickup" && isPickupDisabled) return;
    if (type === "delivery" && isDeliveryDisabled) return;

    setOrderType(type);
    dispatch(setdeliveryType(type));

    // keep existing prop available for other integrations
    try {
      onSubmit?.(type);
    } catch {
      // no-op
    }

    onClose();
  };
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // When popup opens, sync selection BUT DO NOT auto-close when only one option is available
  useEffect(() => {
    if (!open) return;

    // If only one is available, just preselect it (keep popup open)
    if (isPickupDisabled && !isDeliveryDisabled) {
      setOrderType("delivery");
      dispatch(setdeliveryType("delivery"));
      return;
    }
    if (isDeliveryDisabled && !isPickupDisabled) {
      setOrderType("pickup");
      dispatch(setdeliveryType("pickup"));
      return;
    }

    // Otherwise keep Redux active selection
    setOrderType(initialType);
  }, [open, initialType, isPickupDisabled, isDeliveryDisabled, dispatch]);
  // === END SAMPATH ===

  const optionButtonSx = {
    mt: 2.5,
    px: 3,
    py: 1.15,
    minHeight: 46,
    borderRadius: "4px",
    borderWidth: "1.5px",
    borderColor: "rgb(var(--color-primary) / 0.7)",
    backgroundColor: "rgba(255,255,255,0.72)",
    color: "rgba(15,23,42,.92)",
    fontWeight: 700,
    letterSpacing: "0.08em",
    fontSize: "0.98rem",
    textTransform: "none",
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgb(var(--color-primary) / 0.95)",
      borderWidth: "2px",
      backgroundColor: "rgba(255,255,255,0.86)",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      borderColor: "rgba(148,163,184,.28)",
      backgroundColor: "rgba(255,255,255,.5)",
      color: "rgba(15,23,42,.35)",
      boxShadow: "none",
    },
  } as const;

  return (
    <Modal
      open={open}
      onClose={handleModalClose} // === ADDED BY SAMPATH – 2026-01-05 === // === END SAMPATH ===
      className="flex items-center justify-center"
      slotProps={{
        backdrop: {
          onClick: (e) => e.stopPropagation(),
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.69)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
          },
        },
      }}
    >
      <div
        className="w-[94vw] max-w-4xl overflow-hidden rounded-[28px] border border-white/55 shadow-[0_30px_80px_rgba(15,23,42,0.22)]"
        style={{
          background: "rgb(255 255 255 / 0.96)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          fontFamily: '"Noto Sans", sans-serif',
        }}
      >
        <div className="px-5 pt-5 pb-4 sm:px-8 sm:pt-6 sm:pb-5 flex flex-col items-center text-center">
          {settings?.showLogo !== 0 ? (
            <div
              onDoubleClick={() => {
                window.location.href = "/admin/setting/time";
              }}
              className="text-white !px-1.5 relative"
            >
              <img
                key={logoUrl}
                src={logoUrl}
                alt=""
                className="mb-3 max-h-20 max-w-[260px] object-contain"
                loading="eager"
                draggable={false}
              />
            </div>
          ) : (
            <div
              onDoubleClick={() => {
                window.location.href = "/admin/setting/time";
              }}
              className="text-white !px-1.5 relative bg-black/5 p-2 rounded-xl"
            >
              <AdminPanelSettingsIcon className="text-primary" />
            </div>
          )}

          {!!settings.ShowCompanyName && !!companyData?.companyName && (
            <div className="w-full flex flex-col items-center">
              <h1 className="text-xl font-bold text-primary text-center leading-tight break-words whitespace-normal w-full">
                {companyData?.companyName}
              </h1>

              {showLocationUnderName && (
                <p
                  className="mt-1 text-xs text-center text-primary/80 tracking-wide uppercase leading-snug break-words whitespace-normal w-full"
                  title={locationText}
                >
                  {locationText}
                </p>
              )}

              {/* If both services are closed, show one clear red status message */}
              {isAllDisabled && (
                <div className="mt-2 text-sm font-semibold text-red-600 text-center">
                  {allUnavailableLabel}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-4 pb-5 sm:px-8 sm:pb-7">
          {(settings.CloseDeliveryOrder !== 1 || settings.ClosePickupOrder !== 1) && (
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-[24px] sm:text-[28px] font-[900] leading-tight tracking-[-0.03em] text-slate-900">
                Choose Your Order Type
              </h2>
              <p className="mt-2 text-[13px] sm:text-[15px] leading-6 text-slate-600">
                Select whether you'd like to pick up your order or have it delivered.
              </p>
            </div>
          )}

          <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-[1fr_auto_1fr] md:items-start">
            <div
              className="px-4 py-3 text-center"
            >
              <div className="mx-auto flex items-center justify-center gap-3 text-black/70 md:flex-col md:gap-0">
                <LocalMallOutlinedIcon sx={{ fontSize: 30 }} className="text-black/70" />
                <div className="text-[15px] font-[700] tracking-[0.02em] text-black/85 whitespace-nowrap md:mt-3">
                  Order Pickup
                </div>
              </div>

              <Button
                variant="outlined"
                fullWidth
                disabled={isPickupDisabled}
                onClick={() => handleStartOrder("pickup")}
                sx={optionButtonSx}
              >
                Start Your Order
              </Button>

              {isPickupDisabled && (
                <div className="mt-2 text-sm font-semibold text-red-600">
                  {pickupUnavailableLabel}
                </div>
              )}
            </div>

            <div className="hidden md:flex items-center justify-center self-stretch">
              <div className="h-[132px] w-px bg-slate-300/80" />
            </div>

            <div
              className="px-4 py-3 text-center"
            >
              <div className="mx-auto flex items-center justify-center gap-3 text-black/70 md:flex-col md:gap-0">
                <LocalShippingOutlinedIcon sx={{ fontSize: 30 }} className="text-black/70" />
                <div className="text-[15px] font-[700] tracking-[0.02em] text-black/85 whitespace-nowrap md:mt-3">
                  Get Delivery
                </div>
              </div>

              <Button
                variant="outlined"
                fullWidth
                disabled={isDeliveryDisabled}
                onClick={() => handleStartOrder("delivery")}
                sx={optionButtonSx}
              >
                Start Your Order
              </Button>

              {isDeliveryDisabled && (
                <div className="mt-2 text-sm font-semibold text-red-600">
                  {deliveryUnavailableLabel}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default OrderTypePopup;
